import "@blocknote/core/fonts/inter.css";
import { en } from "@blocknote/core/locales";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { useCreateBlockNote } from "@blocknote/react";
import { AIExtension } from "@blocknote/xl-ai";
import { en as aiEn } from "@blocknote/xl-ai/locales";
import "@blocknote/xl-ai/style.css";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import styles from "./editor.module.scss";
import { scriptGenerationHeld } from "./finishedScript";
import { ScriptEditorMenus } from "./ScriptEditorMenus";
import { scriptEditorSchema, scriptStreamTools } from "./scriptAiTools";
import { blocksFromScript, documentToScriptHtml } from "./scriptDocument";
import frameStyles from "./scriptFrame.module.scss";
import { GENERATE_PROMPT } from "./scriptPrompt";
import { createScriptTransport, scriptToolUrl } from "./scriptTransport";

export type ScriptEditorHandle = {
  generate: (scriptConceptId: string) => Promise<string>;
};

export const ScriptEditor = forwardRef<
  ScriptEditorHandle,
  {
    documentId: string;
    seed: string;
    loadKey: string;
    disabled: boolean;
    generating: boolean;
    canRevise: boolean;
    jobId: string;
    onMarkdownChange: (markdown: string) => void;
  }
>(function ScriptEditor(
  {
    documentId,
    seed,
    loadKey,
    disabled,
    generating,
    canRevise,
    jobId,
    onMarkdownChange,
  },
  ref
) {
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const applying = useRef(false);
  const onChangeRef = useRef(onMarkdownChange);
  onChangeRef.current = onMarkdownChange;

  const transport = useMemo(
    () =>
      createScriptTransport(scriptToolUrl("request-revision"), () => ({
        creatorScriptGeneratorJobId: jobIdRef.current,
      })),
    []
  );

  const editor = useCreateBlockNote({
    schema: scriptEditorSchema,
    dictionary: {
      ...en,
      ai: {
        ...aiEn,
        formatting_toolbar: { ai: { tooltip: "Revise with AI" } },
        slash_menu: {
          ...aiEn.slash_menu,
          ai: {
            ...aiEn.slash_menu.ai,
            title: "AI",
            subtext: "Add or change the script",
          },
        },
        ai_menu: {
          ...aiEn.ai_menu,
          input_placeholder: "Type what to add or change…",
        },
      },
    },
    extensions: [
      AIExtension({ transport, streamToolsProvider: scriptStreamTools }),
    ],
  });

  useImperativeHandle(
    ref,
    () => ({
      generate: async (scriptConceptId) => {
        const ai = editor.getExtension(AIExtension);
        if (!ai) throw new Error("Script AI is unavailable");
        const blocks = await blocksFromScript(editor, " ");
        editor.replaceBlocks(editor.document, blocks);

        await ai.invokeAI({
          userPrompt: GENERATE_PROMPT,
          useSelection: false,
          deleteEmptyCursorBlock: false,
          transport: createScriptTransport(
            scriptToolUrl("request-generation"),
            () => ({
              creatorScriptGeneratorJobId: jobIdRef.current,
              conceptId: scriptConceptId,
            })
          ),
        });
        try {
          ai.acceptChanges();
        } catch {
          // The generated blocks are already in the document.
        }
        return documentToScriptHtml(editor);
      },
    }),
    [editor]
  );

  useEffect(() => {
    return () => {
      if (scriptGenerationHeld()) return;
      try {
        void editor
          .getExtension(AIExtension)
          ?.abort()
          ?.catch(() => undefined);
      } catch {
        // Abort is only valid while a request is running.
      }
    };
  }, [editor]);

  useEffect(() => {
    if (generating) return;
    let cancelled = false;
    const apply = async () => {
      try {
        const blocks = await blocksFromScript(editor, seedRef.current);
        if (cancelled) return;
        applying.current = true;
        editor.replaceBlocks(editor.document, blocks);
      } catch {
        // Keep the current document when the saved script cannot be read.
      } finally {
        if (!cancelled) applying.current = false;
      }
    };
    void apply();
    return () => {
      cancelled = true;
      applying.current = false;
    };
  }, [documentId, editor, generating, loadKey]);

  return (
    <div
      className={`${frameStyles.scriptFrame} ${generating ? frameStyles.scriptFrameGenerating : ""}`}
    >
      <div
        className={`${styles.scriptEditor} ${disabled && !generating ? styles.scriptEditorDisabled : ""}`}
      >
        <BlockNoteView
          editor={editor}
          theme="light"
          editable={!disabled}
          formattingToolbar={false}
          slashMenu={false}
          onChange={() => {
            if (applying.current || disabled) return;
            const html = documentToScriptHtml(editor);
            if (html) onChangeRef.current(html);
          }}
        >
          <ScriptEditorMenus editor={editor} canRevise={canRevise} />
        </BlockNoteView>
      </div>
    </div>
  );
});
